// src/utils/saveProject.js
import pako from 'pako';
import { saveAs } from 'file-saver';

/**
 * Safely extract a data-URL string from either a string or {url}.
 * Returns null for any falsy input.
 */
function extractUrl(item) {
  if (!item) return null;
  return typeof item === 'string' ? item : item.url || null;
}

/**
 * Build a clean payload where:
 *  - every image array has only non-null data-URLs
 *  - every single image field is either a data-URL or null
 */
function cleanPayload(projectInfo, rooms) {
  const cleanInfo = { ...projectInfo,
    propertyPhoto: extractUrl(projectInfo.propertyPhoto)
  };

  const cleanRooms = rooms.map(room => {
    const {
      pictures = [],
      observationsPhotos = [],
      iaqReadings,
      ...rest
    } = room;

    // Clean multi-photo arrays, dropping any nulls
    const cleanPictures  = pictures
      .map(extractUrl)
      .filter(url => url);
    const cleanObsPhotos = observationsPhotos
      .map(extractUrl)
      .filter(url => url);

    // Clean IAQ block if present
    let cleanIaq = null;
    if (iaqReadings) {
      const {
        temperatureHumidityPicture,
        pmPicture,
        formaldehydeTvocPictures = [],
        co2AqiPictures = [],
        ...iaqRest
      } = iaqReadings;

      cleanIaq = {
        ...iaqRest,
        temperatureHumidityPicture: extractUrl(temperatureHumidityPicture),
        pmPicture:                  extractUrl(pmPicture),
        formaldehydeTvocPictures:   formaldehydeTvocPictures
                                      .map(extractUrl)
                                      .filter(url => url),
        co2AqiPictures:             co2AqiPictures
                                      .map(extractUrl)
                                      .filter(url => url),
      };
    }

    return {
      ...rest,
      pictures:           cleanPictures,
      observationsPhotos: cleanObsPhotos,
      iaqReadings:        cleanIaq,
    };
  });

  return { projectInfo: cleanInfo, rooms: cleanRooms };
}

export const saveProjectToDisk = (projectInfo, rooms) => {
  const payload    = cleanPayload(projectInfo, rooms);
  const compressed = pako.gzip(JSON.stringify(payload));
  const blob       = new Blob([compressed], { type: 'application/gzip' });

  // friendly filename
  const now      = new Date(projectInfo.dateTime || Date.now());
  const mm       = String(now.getMonth()+1).padStart(2,'0');
  const dd       = String(now.getDate()).padStart(2,'0');
  const initials = projectInfo.assessor
    ? projectInfo.assessor.split(' ').map(n=>n[0]?.toUpperCase()).join('')
    : 'NA';

  const filename =
    `${projectInfo.projectName||'Untitled'}_`+
    `${projectInfo.projectType||'Project'}_`+
    `${mm}-${dd}_${initials}.json.gz`;

  saveAs(blob, filename);
};

export const loadProjectFromFile = async file => {
  const buffer       = await file.arrayBuffer();
  const decompressed = pako.ungzip(new Uint8Array(buffer), { to: 'string' });
  return JSON.parse(decompressed);
};
